import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import useClickOutside from "hooks/useClickOutside";

// Підкомпонент для списку опцій
const OptionsList = React.memo(({ options, selected, onSelect, multiple, mode }) => {
  // Якщо немає опцій, виводимо повідомлення
  if (options.length === 0) {
    return (
      <div className="dropdown__item dropdown__item--no-options">
        <span>Немає опцій</span>
      </div>
    );
  }
  // Виводимо опції
  return (
    <>
      {options.map(({ value, label }) => {
        const isSelected = multiple
          ? Array.isArray(selected) && selected.includes(value)
          : selected === value;

        return (
          <div
            key={value}
            className={`dropdown__item ${isSelected ? "dropdown__item--selected" : ""}`}
            onMouseDown={(e) => mode === "autocomplete" && e.preventDefault()}
            onClick={() => onSelect(value)}
          >
            <span>{label}</span>
          </div>
        );
      })}
    </>
  );
});

// Підкомпонент для режиму select
const SelectField = React.memo(({ label, isOpen, onToggle, disabled, isPlaceholder }) => (
  <div
    className={`dropdown__field-wrapper ${
      disabled ? "dropdown__field-wrapper--disabled" : ""
    }`}
    onClick={disabled ? undefined : onToggle}
  >
    <span
      className={`dropdown__label ${isPlaceholder ? "dropdown__label--placeholder" : ""}`}
    >
      {label}
    </span>
    <span className={`dropdown__arrow ${isOpen ? "up" : "down"}`}>▾</span>
  </div>
));

// Підкомпонент для режиму autocomplete
const AutocompleteInput = ({
  value,
  onInputChange,
  onFocus,
  placeholder,
  isOpen,
  inputRef,
  onKeyDown,
  disabled,
}) => (
  <div
    className={`dropdown__field-wrapper ${
      disabled ? "dropdown__field-wrapper--disabled" : ""
    }`}
    onClick={disabled ? undefined : () => inputRef.current && inputRef.current.focus()}
  >
    <input
      ref={inputRef}
      type="text"
      className="dropdown__input"
      value={value}
      placeholder={placeholder}
      onFocus={disabled ? undefined : onFocus}
      onChange={disabled ? undefined : onInputChange}
      onKeyDown={onKeyDown}
      autoComplete="off"
      disabled={disabled}
    />
    <span className={`dropdown__arrow ${isOpen ? "up" : "down"}`}>▾</span>
  </div>
);

// Головний компонент Dropdown
const Dropdown = ({
  options = [],
  selected,
  onChange,
  placeholder = "Виберіть...",
  mode = "select", // 'select' або 'autocomplete'
  multiple = false,
  disabled = false,
  className = "",
  onKeyDown = () => {},
  // fixed: список позиціонується відносно вікна, а не поля — не ріжеться
  // контейнерами з overflow (таблиці, модалки). listMinWidth — мінімальна
  // ширина такого списку, щоб довгі підписи не обрізались у вузькій колонці
  fixed = false,
  listMinWidth = 0,
  // customOption(query) → { value, label } | null: у режимі autocomplete
  // дозволяє обрати те, чого немає в options (наприклад, id, якого ще не
  // підтягнуло з CRM). Викликається для введеного тексту; якщо повертає
  // варіант, якого ще немає в списку, він додається в кінець
  customOption = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [openDirection, setOpenDirection] = useState("down");
  const [anchor, setAnchor] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Фіксований список «відривається» від поля: при прокрутці чи зміні розміру
  // вікна він лишився б на старому місці — тож закриваємо
  useEffect(() => {
    if (!fixed || !isOpen) return undefined;
    const close = (e) => {
      if (wrapperRef.current && e.target instanceof Node && wrapperRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [fixed, isOpen]);

  // Варіант за значенням: зі списку або «свій» через customOption — інакше
  // після вибору введеного id поле одразу спорожніло б
  const resolveOption = useCallback(
    (value) => {
      const sel = options.find((opt) => opt.value === value);
      if (sel || !customOption || value === null || value === undefined || value === "") return sel;
      const custom = customOption(String(value));
      return custom && custom.value === value ? custom : undefined;
    },
    [options, customOption],
  );

  // Синхронізація inputValue з selected у режимі autocomplete
  useEffect(() => {
    if (mode === "autocomplete" && !multiple) {
      const sel = resolveOption(selected);
      setInputValue(sel ? sel.label.trim() : "");
    }
  }, [selected, resolveOption, mode, multiple]);

  // Фільтрація опцій
  const filteredOptions = useMemo(() => {
    if (mode === "autocomplete") {
      const query = inputValue.trim().toLowerCase();
      // Поки в полі стоїть підпис уже обраного — показуємо весь список,
      // інакше після відкриття видно лише один рядок і нема з чого вибирати
      const current = !multiple && resolveOption(selected);
      if (!query || (current && query === current.label.trim().toLowerCase())) return options;
      const matched = options.filter((opt) => opt.label.toLowerCase().includes(query));
      const custom = customOption ? customOption(inputValue.trim()) : null;
      if (custom && !options.some((opt) => opt.value === custom.value)) matched.push(custom);
      return matched;
    }
    return options;
  }, [options, inputValue, mode, multiple, selected, resolveOption, customOption]);

  // Закриття при кліку поза компонентом
  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
    if (mode === "autocomplete") {
      // Повертаємо inputValue до того, що обране
      if (multiple) {
        setInputValue("");
      } else {
        const sel = resolveOption(selected);
        setInputValue(sel ? sel.label.trim() : "");
      }
    }
  });

  // Обробка зміни в input
  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  }, []);

  // Обробка змін в select
  const handleSelect = useCallback(
    (value) => {
      if (multiple) {
        let newSelected = Array.isArray(selected) ? [...selected] : [];
        if (newSelected.includes(value)) {
          newSelected = newSelected.filter((v) => v !== value);
        } else {
          newSelected.push(value);
        }
        onChange(newSelected);
      } else {
        onChange(value);
        setIsOpen(false);
      }
    },
    [onChange, selected, multiple],
  );

  // Вибір що саме буде відображатис в label
  const displayLabel = useMemo(() => {
    if (multiple) {
      if (Array.isArray(selected) && selected.length > 0) {
        // Знаходимо всі мітки для обраних значень і з’єднуємо комами
        const labels = options
          .filter((opt) => selected.includes(opt.value))
          .map((opt) => opt.label.trim());
        return labels.join(", ");
      }
      return placeholder;
    } else {
      const sel = resolveOption(selected);
      return sel ? sel.label : placeholder;
    }
  }, [selected, options, multiple, placeholder, resolveOption]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const LIST_ESTIMATED_HEIGHT = fixed ? 320 : 200; // max-height списку 20rem

      // Якщо внизу місця мало, а вгорі - достатньо, відкриваємо вгору
      if (spaceBelow < LIST_ESTIMATED_HEIGHT && rect.top > LIST_ESTIMATED_HEIGHT) {
        setOpenDirection("up");
      } else {
        setOpenDirection("down");
      }
      setAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width });
    }
    setIsOpen(true);
  }, [disabled, fixed]);

  // Стиль фіксованого списку: під полем або над ним, не вужчий за поле
  const fixedStyle = useMemo(() => {
    if (!fixed || !anchor) return undefined;
    const width = Math.min(Math.max(anchor.width, listMinWidth), window.innerWidth - 16);
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
    // minWidth: 0 перекриває min-width: 100% зі стилів — для fixed це була б ширина вікна
    const base = { position: "fixed", left, width, minWidth: 0, maxWidth: width };
    return openDirection === "up"
      ? { ...base, top: "auto", bottom: window.innerHeight - anchor.top + 4 }
      : { ...base, top: anchor.bottom + 4 };
  }, [fixed, anchor, listMinWidth, openDirection]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openDropdown();
    }
  }, [isOpen, openDropdown]);

  const handleFocus = useCallback(() => {
    openDropdown();
  }, [openDropdown]);

  return (
    <div
      className={`dropdown ${mode}-mode ${
        disabled ? "dropdown--disabled" : ""
      } ${className}`}
      ref={wrapperRef}
    >
      {mode === "select" ? (
        <SelectField
          label={displayLabel}
          isPlaceholder={displayLabel === placeholder}
          isOpen={isOpen}
          onToggle={handleToggle}
          disabled={disabled}
        />
      ) : (
        <AutocompleteInput
          value={inputValue}
          onInputChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          inputRef={inputRef}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      )}

      {isOpen && (
        <div
          className={`dropdown__list-container ${mode}-list dropdown__list-container--${openDirection}`}
          style={fixedStyle}
        >
          <OptionsList
            options={filteredOptions}
            selected={selected}
            onSelect={handleSelect}
            multiple={multiple}
            mode={mode}
          />
        </div>
      )}
    </div>
  );
};

export default Dropdown;
